/* eslint-disable no-await-in-loop */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError } from '../../components/types';
import { RootState } from '../../app/store';
import { apiBaseUrl } from '../../app/config';
import {
  IVideoStreamData,
  uploadVideoStreamApi,
  removeVideoStreamApi,
  readAvailableStreamsApi,
  playVideoStreamReqTokenApi,
} from './manageVideoContent.api';
import { actions as appActions } from '../app/app.slice';

export interface IVideoUploadedStream {
  id: string;
  filename: string;
  description: string;
  uploadCompleted: string;
  size: number;
  date: Date;
}

export interface IProgressingUpload {
  stream: IVideoStreamData;
  uploadedBytes: number;
}

export interface IManageVideoContentViewState {
  uploads: Array<IVideoUploadedStream>;
  loading: boolean;
  success: boolean;
  isUploadVideoStreamModalOpen: boolean;
  error: ApiError;
  inProgressUploads: Array<IProgressingUpload>;
}

const initialState: IManageVideoContentViewState = {
  uploads: [],
  loading: false,
  success: false,
  isUploadVideoStreamModalOpen: false,
  error: null,
  inProgressUploads: [],
};

function readChunk(
  file: File,
  start: number,
  end: number
): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer)
        resolve(new Uint8Array(reader.result));
      else reject(new Error('Failed to read file'));
    };

    reader.onerror = () => reject(new Error('Error reading file chunk'));
    reader.readAsArrayBuffer(file.slice(start, end));
  });
}

export const readManageVideoContentAsync = createAsyncThunk(
  'videoStream/fetch',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await readAvailableStreamsApi(data);
      return response.data;
    } catch (error: ApiError) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const uploadManageVideoContentAsync = createAsyncThunk(
  'videoStream/upload',
  async (data: IVideoStreamData, { rejectWithValue, dispatch }) => {
    try {
      const chunkSize = 512 * 1024;
      const chunksCount = Math.ceil(data.file.size / chunkSize);

      for (let cIdx = 0; cIdx < chunksCount; cIdx += 1) {
        const start = cIdx * chunkSize;
        const sz = Math.min(data.file.size - start, chunkSize);
        const chunk = await readChunk(data.file, cIdx * chunkSize, start + sz);

        const formData = {
          chunkIndex: cIdx,
          totalChunks: chunksCount,
          filename: data.name,
          description: data.description,
          account: data.account,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          chunk: Array.from(chunk),
        };

        dispatch(
          actions.updateManageVideoContentUpload({
            stream: data,
            uploadedBytes: start,
          })
        );
        const response = await uploadVideoStreamApi(formData);
        if (cIdx === chunksCount - 1) {
          return response.data;
        }
      }

      throw new Error('upload_unknown_error');
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteManageVideoContentAsync = createAsyncThunk(
  'videoStream/delete',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await removeVideoStreamApi(data);

      // fetch all video after successfully uploaded
      dispatch(readManageVideoContentAsync({ account: data.account }));
      dispatch(
        appActions.triggerAlert({
          type: 'success',
          childern: 'successfully_removed_video_stream',
        })
      );
      return response.data;
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const playManageVideoContentAsync = createAsyncThunk(
  'videoStream/play',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await playVideoStreamReqTokenApi(data);
      return response.data;
    } catch (error: ApiError) {
      dispatch(
        appActions.triggerAlert({
          type: 'error',
          childern: error?.response?.data?.error || 'error_occurred',
        })
      );
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const ManageVideoContentSlice = createSlice({
  name: 'videoStream',
  initialState,
  reducers: {
    showManageVideoContentUpload: (state) => {
      return { ...state, isUploadVideoStreamModalOpen: true };
    },
    closeManageVideoContentUpload: (state) => {
      return { ...state, isUploadVideoStreamModalOpen: false };
    },
    updateManageVideoContentUpload: (state, data) => {
      const uploadings = state.inProgressUploads.slice();
      const el = uploadings.find(
        (p) => p.stream.name === data.payload.stream.name
      );
      if (el == null) {
        uploadings.unshift({
          ...data.payload,
          uploadedBytes: data.payload.uploadedBytes,
        });
      } else {
        el.uploadedBytes = data.payload.uploadedBytes;
      }
      state.inProgressUploads = uploadings;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(readManageVideoContentAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(readManageVideoContentAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.uploads = action?.payload?.data;

      let uploadings = state.inProgressUploads.slice();
      state.uploads.forEach((e) => {
        uploadings = uploadings.filter((i) => i.stream.name !== e.filename);
      });
      state.inProgressUploads = uploadings;
    });
    builder.addCase(readManageVideoContentAsync.rejected, (state, action) => {
      state.loading = false;
      state.uploads = [];
      state.error = action.payload;
    });

    builder.addCase(uploadManageVideoContentAsync.pending, (state, action) => {
      state.loading = true;
      state.isUploadVideoStreamModalOpen = false;
      state.error = null;
    });

    builder.addCase(
      uploadManageVideoContentAsync.fulfilled,
      (state, action) => {
        state.loading = false;
        state.error = null;
        state.isUploadVideoStreamModalOpen = false;
        state.uploads = action?.payload?.data;

        let uploadings = state.inProgressUploads.slice();
        state.uploads.forEach((e) => {
          uploadings = uploadings.filter((i) => i.stream.name !== e.filename);
        });
        state.inProgressUploads = uploadings;
      }
    );

    builder.addCase(uploadManageVideoContentAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = null;
      state.isUploadVideoStreamModalOpen = false;
      state.success = false;
      state.error = action.payload;
    });

    builder.addCase(deleteManageVideoContentAsync.pending, (state, action) => {
      state.loading = false;
    });
    builder.addCase(
      deleteManageVideoContentAsync.fulfilled,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(deleteManageVideoContentAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(playManageVideoContentAsync.pending, (state, action) => {
      state.loading = false;
    });
    builder.addCase(playManageVideoContentAsync.fulfilled, (state, action) => {
      state.loading = false;
      window.open(
        `${apiBaseUrl}/videocontent/play?idt=${action.payload.data}`,
        '_blank',
        'noopener,noreferrer'
      );
      console.log(action.payload.data);
    });
    builder.addCase(playManageVideoContentAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { actions } = ManageVideoContentSlice;

export const selectManageVideoContent = (
  state: RootState
): IManageVideoContentViewState => state.manageVideoContent;

export default ManageVideoContentSlice.reducer;
